<script setup lang="ts">
import { computed, ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { formatDate } from '@/utils/dateUtils';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';

interface Props {
  excludeRooms: object;
  maxCharacters: number;
}
const props = defineProps<Props>();

const visibleReadMore = ref(false);
const currentLanguage = getCurrentLocale();

const excludeHotelRoom = computed(() => {
  return formatRoom(props.excludeRooms);
});

const separateCharacters = computed(() => {
  let text = formatListRoomString(excludeHotelRoom.value).slice(0, props.maxCharacters);
  if (text.length < formatListRoomString(excludeHotelRoom.value).length) {
    text += '...';
  }
  return text;
});

const formatRoomExclusionData = (categories: object[]) => {
  const formatRoomExclusion: any = [];
  categories?.forEach((category: any) => {
    category?.ranges?.forEach((time: any) => {
      formatRoomExclusion.push({
        category: category.category,
        name: category.name,
        dateDep: formatDate(time.dateDep, 'dd-LLL-yyyy', false, currentLanguage),
        dateRet: formatDate(time.dateRet, 'dd-LLL-yyyy', false, currentLanguage)
      });
    });
  });

  return formatRoomExclusion;
};

const formatManualRoomExclusionData = (data: object[]) => {
  const formatRoomExclusion: any = [];
  data?.forEach((item: any) => {
    item?.categories?.forEach((category: any) => {
      item?.ranges?.forEach((time: any) => {
        formatRoomExclusion.push({
          category: category,
          name: '',
          dateDep: formatDate(time.dateDep, 'dd-LLL-yyyy', false, currentLanguage),
          dateRet: formatDate(time.dateRet, 'dd-LLL-yyyy', false, currentLanguage)
        });
      });
    });
  });

  return formatRoomExclusion;
};

const formatRoom = (data: object) => {
  const formatRoomDefault = data?.categories ? formatRoomExclusionData(data?.categories) : [];
  const formatRoomManual = data?.manualCategories ? formatManualRoomExclusionData(data?.manualCategories) : [];
  return [...formatRoomDefault, ...formatRoomManual];
};

const formatListRoomString = (listRoom: any) => {
  const format = listRoom?.map((room: any) => {
    return `${room.category}(${room.dateDep} to ${room.dateRet})`;
  });
  return format.join('\n') ?? '';
};

const visibleReadMoreFunc = (isVisible: boolean) => {
  visibleReadMore.value = isVisible;
};

const formatDataModal = (listRooms: any) => {
  return listRooms.reduce((acc: any, { category, name, dateDep, dateRet }: any) => {
    let roomFilter = acc.find((room: any) => room.category === category);
    if (!roomFilter) {
      roomFilter = { category, name, ranges: [] };
      acc.push(roomFilter);
    }
    roomFilter.ranges.push(`${dateDep} to ${dateRet}`);
    return acc;
  }, []);
};
</script>
<template>
  <div class="room-exclusions-modal">
    <div>
      <p class="title break-spaces">
        {{ separateCharacters }}
        <span v-if="separateCharacters.length < formatListRoomString(excludeHotelRoom).length" class="view-more">
          <a class="text-blue-600 font-bold underline cursor-pointer text-left uppercase" @click="visibleReadMoreFunc(true)">{{ $t('common.labels.see_more') }}</a>
        </span>
      </p>
    </div>
    <Dialog :style="{ maxWidth: '50rem' }" :header="$t('common.labels.room_exclusions')" v-model:visible="visibleReadMore" :modal="true" class="tcs long-text-modal">
      <div class="room-exclusions mt-3" v-for="(room, index) in formatDataModal(excludeHotelRoom)" :key="index">
        <p class="room-name m-0 underline">{{ `${room.name}(${room.category}):` }}</p>
        <p class="blackout-date m-0">
          {{ $t('hotel_contract.black_out_date') }} : <span>{{ room.ranges.join(', ') }}</span>
        </p>
      </div>
      <div class="flex justify-content-center mt-2">
        <Button :label="$t('common.buttons.close')" class="p-button" @click="visibleReadMoreFunc(false)" />
      </div>
    </Dialog>
  </div>
</template>
<style lang="scss">
.long-text-modal {
  width: max(300px, 50vw);
}

.break-spaces {
  white-space: break-spaces;
}
</style>
